import EditorErrorIcon from '@atlaskit/icon/glyph/editor/error';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import {
  attachClosestEdge,
  type Edge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useContextualMenu } from '@databases/contextual-menu';
import { useBooleanFeatureFlag } from '@databases/feature-flags/hooks';
import { messages } from '@databases/intl/messages';
import Z_INDEX from '@databases/layout/z-index';
import { useHoverState } from '@databases/react';
import { isModifierKeyPressed } from '@databases/react';
import { DefaultRootState, defaultStoreUtils } from '@databases/redux';
import {
  addEntryToValueRange,
  selectEntryMulti,
  selectEntrySingle,
  setSoftSelection,
} from '@databases/redux/cellSelections/actions';
import {
  selectSelectedEntireEntryIds,
  selectIsEntireEntrySelected,
} from '@databases/redux/cellSelections/selectors';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { useScope } from '@databases/scope';
import useView from '@databases/views/useView';
import composeRefs from '@seznam/compose-react-refs';
import classNames from 'classnames';
import React, { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { useIntl } from 'react-intl-next';
import styled, { css } from 'styled-components';

import { TABLE_CLASSNAMES } from '../../../../constants';
import { DragType, setPublicDragData } from '../../../drag-utils';
import { getBodyFromTable, getRowsFromBody, getTableFromBodyRow } from '../../../table';
import { blurActiveElement, isApple, removeTextSelection } from '../../../utils';
import { IGNORED_TABLE_ELEMENT_CLASS } from '../../TableResizer';
import SelectEntryMenu from '../contextual-menu/SelectEntryMenu';
import { Position } from '../contextual-menu/type';

import EntryNumber, { EntryIsSelectedMap } from './EntryNumber';
import TableLayoutCellWithMenu from './TableLayoutCellWithMenu';

const SELECT_BUTTON_PADDING = 9;
const SELECT_BUTTON_WIDTH = 15;

export const DisabledTooltipContainer = styled.div`
  position: absolute;
  z-index: ${Z_INDEX.TABLE_VIEW_SELECTION_TOOLBAR};
  width: max-content;
  padding: 15px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: -8px;

  border-radius: 4px;
  background: ${token('elevation.surface')};
  box-shadow: 0 0 1px 0 rgba(9, 30, 66, 0.31), 0 2px 16px 0 rgba(9, 30, 66, 0.12),
    0 2px 8px 0 rgba(9, 30, 66, 0.05);
`;

type InsertRowIndicatorProps = {
  insertIndicatorPosition: Position | undefined;
};

const InsertRowIndicator = css<InsertRowIndicatorProps>`
  :after {
    content: ' ';
    position: absolute;
    left: 0;
    width: calc(100% - 1px);
    height: 2px;
    background-color: ${colors.B200};
    opacity: 0;

    ${({ insertIndicatorPosition }) =>
      insertIndicatorPosition === Position.BEFORE
        ? css`
            opacity: 1;
            top: -0.5px;
          `
        : insertIndicatorPosition === Position.AFTER &&
          css`
            opacity: 1;
            bottom: -0.5px;
          `};
  }
`;

interface TrProps extends InsertRowIndicatorProps {
  isInitialized: boolean;
  isFilterOrSortingActive: boolean;
}

export const Tr = styled.tr<TrProps>`
  transition: opacity 1s;
  opacity: ${({ isInitialized }) => (isInitialized ? '1' : '0')};
  position: relative;

  ${InsertRowIndicator};

  &.${TABLE_CLASSNAMES.SHOW_AS_DISABLED} {
    * {
      cursor: ${({ isFilterOrSortingActive }) =>
        isFilterOrSortingActive ? 'not-allowed' : 'grabbing'} !important;
    }
  }

  &:hover:not(.${TABLE_CLASSNAMES.SHOW_AS_DISABLED}) {
    .${TABLE_CLASSNAMES.SELECT_BUTTON} {
      display: flex;
    }
  }
`;

type DragState =
  | {
      type: 'idle';
    }
  | {
      type: 'dragging';
    }
  | {
      type: 'is-over';
      closestEdge: Edge | null;
    };

export interface TableLayoutRowProps {
  contentId: string;
  entryId: string;
  isContentEditable: boolean;
  canEditFields: boolean;
  canEditContent: boolean;
  editingIds?: MutableRefObject<Set<string>>;
  isDraggingActive: boolean;
  isLastInColumn: boolean;
  onDragStart?: (e: React.DragEvent, entryId: string) => void;
  onDrag?: (e: React.DragEvent, entryId: string) => void;
  onDragEnd?: (e: React.DragEvent, entryId: string) => void;
  containerWidth: number;
  currentViewId: string;
  index: number;
  entryIsSelectedMap: EntryIsSelectedMap;
}

const TableLayoutRow = React.memo(
  React.forwardRef<HTMLElement, TableLayoutRowProps>(
    (
      {
        contentId,
        entryId,
        isContentEditable,
        canEditContent,
        canEditFields,
        editingIds,
        isDraggingActive,
        isLastInColumn,
        onDragStart,
        onDrag,
        onDragEnd,
        containerWidth,
        currentViewId,
        index,
        entryIsSelectedMap,
      },
      ref
    ) => {
      const {
        view: { settings: viewSettings },
      } = useView();
      const { formatMessage } = useIntl();
      const [isHoverDragButton, hoverProps] = useHoverState();
      const scope = useScope();
      const [dragState, setDragState] = useState<DragState>({ type: 'idle' });
      const isPragmaticDndEnabled = useBooleanFeatureFlag(
        'database-frontend-cell-interactions-m0'
      );

      const [
        fieldIds,
        isSelected,
        selectedRows,
        isFilterOrSortingActive,
        isFirstEntry,
        isLastEntry,
      ] = defaultStoreUtils.useEqualSelector((state: DefaultRootState) => {
        const database = selectDatabaseDraftByContentId(state, {
          contentId,
          scope,
          viewSettings,
          includeDeleted: true,
        });
        const isSelected = selectIsEntireEntrySelected(state, {
          entryId,
        });
        return [
          database.fields,
          isSelected,
          selectSelectedEntireEntryIds(state, {
            contentId,
          }),
          !!viewSettings?.filter || !!viewSettings?.sort,
          database.entries.indexOf(entryId) === 0,
          database.entries.indexOf(entryId) === database.entries.length - 1,
        ] as const;
      });

      const [isInitialized, setIsInitialized] = useState(true);
      useEffect(() => {
        setIsInitialized(true);
      }, []);

      const dispatch = defaultStoreUtils.useDispatch();
      const handleSelectRow = useCallback(
        (e?: React.MouseEvent | React.KeyboardEvent) => {
          const isMultiSelectOn =
            !!e && isModifierKeyPressed(e, [isApple ? 'metaKey' : 'ctrlKey', 'shiftKey']);
          const isRangeSelectOn = !!e && isModifierKeyPressed(e, ['shiftKey']);
          // Blur the active input element when the cell selection mode has been 'cell' before
          blurActiveElement();
          removeTextSelection();
          if (isRangeSelectOn) {
            dispatch(
              selectEntryMulti({
                contentId,
                entryId,
                isRangeSelect: !isMultiSelectOn,
              })
            );
          } else {
            dispatch(
              selectEntrySingle({
                contentId,
                entryId,
              })
            );
          }
          dispatch(setSoftSelection({ value: false }));
        },
        [contentId, dispatch, entryId]
      );

      const handleSelectCells = useCallback(
        () =>
          dispatch(
            addEntryToValueRange({
              contentId,
              entryId,
            })
          ),
        [dispatch, contentId, entryId]
      );

      const { closeMenu } = useContextualMenu();

      const dragImage = useRef<HTMLElement>();
      const trRef = useRef<HTMLTableRowElement>(null);
      const dragImageRootRef = useRef<Root>();

      const handleDragStart = useCallback(
        (e: React.DragEvent) => {
          if (e.target !== e.currentTarget) {
            // Ignore dragging descendent elements
            return;
          }
          removeTextSelection();
          closeMenu();

          const tr = e.target as HTMLTableRowElement;
          const table = getTableFromBodyRow(tr);
          const tbody = getBodyFromTable(table);
          const trs = getRowsFromBody(tbody);
          const selectedTrs = trs.filter((el) =>
            el.classList.contains(TABLE_CLASSNAMES.IS_SELECTED)
          );
          const currentIndexInSelectedTrs = selectedTrs.indexOf(trRef!.current!);
          const selectedTrsIndexOffsetHeight = selectedTrs
            .slice(0, currentIndexInSelectedTrs + 1)
            .reduce((p, c) => p + c.clientHeight, 0);

          const tBody = document.createElement('tbody');
          // clone the table element to get the table styles (but do not include descendants)
          const tableClone = table?.cloneNode(false) as HTMLTableElement;
          const tbodyClone = tbody?.cloneNode(true) as HTMLTableSectionElement;
          const clonedTrs = getRowsFromBody(tbodyClone);
          const clonedSelectedTrs = clonedTrs.filter((el) =>
            el.classList.contains(TABLE_CLASSNAMES.IS_SELECTED)
          ) as HTMLElement[];

          const originalWidths = Array.from(tr.cells).map((cell) => cell.offsetWidth);
          // Ignore widths of row number and selector cells
          const dragImageWidth =
            originalWidths.slice(2).reduce((sum, num) => sum + num, 0) -
            SELECT_BUTTON_PADDING +
            1;
          const dragImageWidthForScrollableFields =
            containerWidth - 10 - SELECT_BUTTON_PADDING - SELECT_BUTTON_WIDTH;

          const ignoredTrs = clonedSelectedTrs.flatMap(
            (el: HTMLElement) =>
              Array.from(
                el.querySelectorAll(`.${IGNORED_TABLE_ELEMENT_CLASS}`)
              ) as HTMLElement[]
          );

          ignoredTrs.forEach((ignoredTr: HTMLElement) => {
            ignoredTr.style.width = '0';
          });

          const ignoredEntryNumbers = clonedSelectedTrs.flatMap(
            (el: HTMLElement) =>
              Array.from(el.querySelectorAll('.entry-number-table-data')) as HTMLElement[]
          );

          ignoredEntryNumbers.forEach((entryNumber: HTMLElement) => {
            entryNumber.style.opacity = '0';
          });

          // resize table to just display values, not row number or space for selector
          clonedSelectedTrs.forEach((el) => {
            const valueOnlyGrid = [0, 0, ...originalWidths.slice(2)];

            const valueOnlyGridTemplateColumns = valueOnlyGrid.map(
              (width) => `${width}px`
            );
            el.style.gridTemplateColumns = valueOnlyGridTemplateColumns.join(' ');
          });

          // select handle
          const selectButton = clonedSelectedTrs
            .find((el) => el.draggable)
            ?.querySelector(`.${TABLE_CLASSNAMES.SELECT_BUTTON}`) as HTMLElement | null;
          selectButton?.classList.add(TABLE_CLASSNAMES.SHOW_AS_DRAGGING);
          if (selectButton) {
            selectButton.style.left = '-9px';
          }
          dragImage.current = document.createElement('div');
          table?.parentElement?.append(dragImage.current);

          tableClone.append(tBody);
          // eslint-disable-next-line canvas/no-spread-arguments
          tBody.append(...clonedSelectedTrs);
          dragImage.current.append(tableClone);
          if (!isFilterOrSortingActive) {
            Object.assign(dragImage.current.style, {
              // dragImageWidth will be used when table does not warrant field scrolling
              width: `${Math.min(dragImageWidth, dragImageWidthForScrollableFields)}px`,
              overflow: 'hidden',
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: Z_INDEX.DRAG_IMAGE_ELEMENT,
              paddingLeft: `${SELECT_BUTTON_PADDING}px`,
            });
            e.dataTransfer.setDragImage(
              dragImage.current,
              SELECT_BUTTON_WIDTH / 2,
              selectedTrsIndexOffsetHeight - tr?.clientHeight / 2
            );
          } else {
            // Use a error message as the drag image when sorting or filtering is active
            dragImageRootRef.current = createRoot(dragImage.current);
            Object.assign(dragImage.current.style, {
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: Z_INDEX.DRAG_IMAGE_ELEMENT,
            });
            dragImageRootRef.current.render(
              <DisabledTooltipContainer isEntryCardActionBar={false}>
                <EditorErrorIcon label="" primaryColor={colors.R300} />
                <div>
                  {formatMessage(
                    messages['table-layout.reset-filter-to-enable-row-sorting']
                  )}
                </div>
              </DisabledTooltipContainer>
            );
            e.dataTransfer.setDragImage(dragImage.current, 0, 25);
          }

          setPublicDragData(e.dataTransfer, {
            type: DragType.ENTRY_ROWS,
            contentId,
            draggingIds: selectedRows,
            dragOverId: entryId,
            height: trRef.current!.offsetHeight,
            width: trRef.current!.offsetWidth,
          });

          onDragStart?.(e, entryId);
        },
        [
          closeMenu,
          containerWidth,
          contentId,
          entryId,
          formatMessage,
          isFilterOrSortingActive,
          onDragStart,
          selectedRows,
        ]
      );

      const handleDrag = useCallback(
        (e: React.DragEvent) => {
          if (e.target !== e.currentTarget) {
            // Ignore dragging descendent elements
            return;
          }

          onDrag?.(e, entryId);
        },
        [entryId, onDrag]
      );

      const handleDragEnd = useCallback(
        (e: React.DragEvent) => {
          if (e.target !== e.currentTarget) {
            // Ignore dragging descendent elements
            return;
          }

          if (dragImage.current) {
            dragImage.current.remove();
            dragImage.current = undefined;
          }

          if (dragImageRootRef.current) {
            dragImageRootRef.current.unmount();
            dragImageRootRef.current = undefined;
          }

          onDragEnd?.(e, entryId);
        },
        [entryId, onDragEnd]
      );

      const showRowAsDisabled =
        isSelected &&
        (isPragmaticDndEnabled ? dragState.type === 'dragging' : isDraggingActive);

      const [isHoverAddRowBeforeButton, addRowBeforeButtonHoverProps] = useHoverState();
      const [isHoverAddRowAfterButton, addRowAfterButtonHoverProps] = useHoverState();

      // Pragmatic drag and drop
      useEffect(() => {
        if (!trRef.current) {
          return;
        }
        const element = trRef.current;

        return combine(
          draggable({
            element,
            getInitialData() {
              return { type: DragType.ENTRY_ROWS, index, entryId };
            },
            onDragStart() {
              if (isPragmaticDndEnabled) {
                setDragState({ type: 'dragging' });
              }
            },
            onDrop() {
              setDragState({ type: 'idle' });
            },
          }),
          dropTargetForElements({
            element,
            canDrop({ source }) {
              return (
                (source.data.type === DragType.ENTRY_ROWS &&
                  source.data.entryId !== entryId &&
                  isPragmaticDndEnabled) ||
                source.data.type === DragType.FILL_VALUES
              );
            },
            getData({ input, element }) {
              const data = { entryId, index };
              return attachClosestEdge(data, {
                input,
                element,
                allowedEdges: ['top', 'bottom'],
              });
            },
            onDragEnter(args) {
              if (
                args.source.data.type === DragType.ENTRY_ROWS &&
                isPragmaticDndEnabled
              ) {
                setDragState({
                  type: 'is-over',
                  closestEdge: extractClosestEdge(args.self.data),
                });
              } else if (args.source.data.type === DragType.FILL_VALUES) {
                const destination = args.location.current.dropTargets[0];

                if (destination) {
                  handleSelectCells();
                }
              }
            },
            onDrag(args) {
              if (
                args.source.data.type === DragType.ENTRY_ROWS &&
                isPragmaticDndEnabled
              ) {
                const closestEdge: Edge | null = extractClosestEdge(args.self.data);

                // only update react state if the `closestEdge` changes
                setDragState((current) => {
                  if (current.type !== 'is-over') {
                    return current;
                  }
                  if (current.closestEdge === closestEdge) {
                    return current;
                  }
                  return {
                    type: 'is-over',
                    closestEdge,
                  };
                });
              }
            },
            onDragLeave() {
              setDragState({ type: 'idle' });
            },
            onDrop() {
              setDragState({ type: 'idle' });
            },
          })
        );
      }, [index, ref, entryId, handleSelectCells, isPragmaticDndEnabled]);

      return (
        <Tr
          ref={composeRefs(ref, trRef)}
          isInitialized={isInitialized}
          isFilterOrSortingActive={isFilterOrSortingActive}
          insertIndicatorPosition={
            isHoverAddRowBeforeButton
              ? Position.BEFORE
              : isHoverAddRowAfterButton
              ? Position.AFTER
              : undefined
          }
          draggable={canEditContent && isHoverDragButton}
          onDragStart={handleDragStart}
          onDrag={handleDrag}
          onDragEnd={handleDragEnd}
          className={classNames({
            [TABLE_CLASSNAMES.IS_SELECTED]: isSelected,
            [TABLE_CLASSNAMES.SHOW_AS_DISABLED]: showRowAsDisabled,
          })}
        >
          {canEditContent && (
            <SelectEntryMenu
              contentId={contentId}
              scope={scope}
              currentViewId={currentViewId}
              canEditContent={canEditContent}
              entryId={entryId}
              hoverProps={hoverProps}
              handleSelectEntry={handleSelectRow}
              dropIndicatorEdge={
                dragState.type === 'is-over' ? dragState.closestEdge : null
              }
            />
          )}
          <EntryNumber
            entryId={entryId}
            isSelected={isSelected}
            contentId={contentId}
            index={index}
            isDraggingActive={
              isPragmaticDndEnabled ? dragState.type === 'dragging' : isDraggingActive
            }
            entryIsSelectedMap={entryIsSelectedMap}
            isFirstEntry={isFirstEntry}
            isLastEntry={isLastEntry}
          />
          {fieldIds.map((fieldId) => (
            <TableLayoutCellWithMenu
              key={fieldId}
              contentId={contentId}
              entryId={entryId}
              fieldId={fieldId}
              isContentEditable={isContentEditable}
              canEditContent={canEditContent}
              canEditFields={canEditFields}
              editingIds={editingIds}
              isFirstInRow={fieldIds[0] === fieldId}
              isLastInRow={fieldIds[fieldIds.length - 1] === fieldId}
              isLastInColumn={isLastInColumn}
              isSelectionDisabled={
                isPragmaticDndEnabled ? dragState.type === 'dragging' : isDraggingActive
              }
              isInsertContentDisabled={
                isPragmaticDndEnabled ? dragState.type === 'dragging' : isDraggingActive
              }
              showAsDisabled={showRowAsDisabled}
              addRowBeforeButtonHoverProps={addRowBeforeButtonHoverProps}
              addRowAfterButtonHoverProps={addRowAfterButtonHoverProps}
              currentViewId={currentViewId}
              dropIndicatorEdge={
                dragState.type === 'is-over' ? dragState.closestEdge : null
              }
            />
          ))}
          {
            <td
              className={classNames([TABLE_CLASSNAMES.ACTIONS], {
                [IGNORED_TABLE_ELEMENT_CLASS]: true,
              })}
            />
          }
        </Tr>
      );
    }
  )
);

export default TableLayoutRow;
